import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client.js";
import { isUrgentByDefault } from "@/constants/alertConfig.js";

/**
 * useAlertUrgency — qué tipos de alerta son urgentes en esta flota.
 *
 * "Urgente" es una sola cosa en toda la app: la alerta suena aunque el teléfono
 * esté bloqueado, en silencio o en No molestar, se anuncia encima de lo que el
 * usuario esté haciendo y se locuta por el canal de alarma. Lo que no es
 * urgente entra a la bandeja sin interrumpir.
 *
 * Es configuración de FLOTA, no preferencia personal —"esto justifica levantar
 * a alguien a las 3 a.m." lo decide la operación—, así que se lee en las dos
 * puntas: la pantalla de Control que la edita y las pantallas personales que
 * solo la muestran como etiqueta ("Urgente" / "Normal") para explicar por qué
 * una alerta suena distinto que otra.
 *
 * El backend responde el mapa COMPLETO con el default ya aplicado, así que la
 * UI nunca tiene que adivinar. Mientras la respuesta no llegó —o si falla— se
 * usa el default del código (`isUrgentByDefault`), que es el mismo espejo de la
 * política del backend que ya usaba la locución.
 */

// ── Petición compartida entre montajes ───────────────────────────────────────
//
// El hook se monta en varios lados a la vez —la pantalla de flota, la de
// canales, y `useVoiceAnnouncements` que vive en el AppShell de toda la app— y
// cada montaje pedía lo suyo. Medido en el navegador: OCHO GET del mismo mapa
// al abrir Control → Alertas (los montajes, por dos por el StrictMode de
// desarrollo). Es el mismo dato para todos y cambia solo cuando alguien lo
// edita, así que la promesa en vuelo se comparte y la respuesta queda cacheada.
//
// `reload()` y cada escritura invalidan, de modo que "cacheado" nunca significa
// "viejo": el que edita ve su cambio y el resto lo toma en el próximo montaje.
let enVuelo = null;
let cacheado = null;
// Sube en cada invalidación. Una respuesta que salió con una generación vieja
// devuelve su dato a quien lo estaba esperando, pero NO se guarda: soltar la
// referencia a la promesa no la cancela, y sin este sello una petición que había
// salido ANTES de una escritura volvía después y repoblaba la caché con el mapa
// previo — que es exactamente el valor que la invalidación vino a tirar.
let generacion = 0;

function pedirUrgencia() {
  if (cacheado) return Promise.resolve(cacheado);
  if (!enVuelo) {
    const gen = generacion;
    enVuelo = api
      .getAlertUrgency()
      .then((data) => {
        if (gen === generacion) cacheado = data;
        return data;
      })
      .finally(() => {
        if (gen === generacion) enVuelo = null;
      });
  }
  return enVuelo;
}

/**
 * Tira el mapa cacheado. La llaman `reload` y cada escritura.
 *
 * Suelta TAMBIÉN la petición en vuelo: si no, un `reload` disparado mientras la
 * primera todavía viaja se quedaría esperando esa —la que justamente se quiere
 * reemplazar— y devolvería el valor viejo.
 */
export function invalidateAlertUrgency() {
  generacion += 1;
  cacheado = null;
  enVuelo = null;
}

export function useAlertUrgency() {
  const [urgency, setUrgency] = useState(cacheado);
  const [loading, setLoading] = useState(cacheado == null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    invalidateAlertUrgency();
    setLoading(true);
    setError(null);
    const gen = generacion;
    try {
      const data = await pedirUrgencia();
      // Mismo sello que el montaje: si mientras tanto hubo una escritura, este
      // mapa ya nació viejo y pintarlo desharía el cambio recién guardado.
      if (gen === generacion) setUrgency(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // El mismo sello que usa la caché: si entre el pedido y la respuesta hubo
    // una escritura, ese mapa es anterior al cambio y pintarlo hacía que un
    // guardado exitoso pareciera revertirse solo.
    const gen = generacion;
    pedirUrgencia()
      .then((data) => {
        if (!cancelled && gen === generacion) setUrgency(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * ¿Es urgente este tipo? Responde SIEMPRE, con o sin datos cargados: quien
   * pregunta está decidiendo por qué canal de audio hablar o qué etiqueta
   * dibujar, y "no sé todavía" no es una respuesta que sirva ahí.
   */
  const isUrgent = useCallback(
    (alertType) => urgency?.[alertType]?.urgent ?? isUrgentByDefault(alertType),
    [urgency],
  );

  /**
   * Marca un tipo como urgente o normal. `null` borra la elección y vuelve al
   * default de la plataforma.
   */
  const setUrgent = useCallback(
    async (alertType, value) => {
      // `habia` distingue "el tipo tenía otra entrada" de "no había ninguna" —
      // el segundo caso pasa si el mapa todavía no cargó o si el GET falló. Sin
      // la distinción, el rollback se salteaba y la pantalla quedaba mostrando
      // una urgencia que el servidor nunca aceptó: un SOS rotulado "Normal"
      // después de un 403.
      const habia = Boolean(urgency && Object.hasOwn(urgency, alertType));
      const previous = habia ? urgency[alertType] : null;

      // Optimista: el interruptor tiene que responder al toque, no al round-trip.
      setUrgency((prev) => ({
        ...prev,
        [alertType]: {
          urgent: value == null ? isUrgentByDefault(alertType) : value,
          is_default: value == null,
        },
      }));
      setSaving(true);
      try {
        await api.updateAlertUrgency({ [alertType]: value });
        // El mapa cacheado quedó viejo para el próximo montaje. No se repide
        // acá: este componente ya tiene el valor nuevo aplicado de forma
        // optimista, y un GET extra por cada toque de interruptor es justo lo
        // que este caché vino a evitar.
        invalidateAlertUrgency();
      } catch (err) {
        setUrgency((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          if (habia) next[alertType] = previous;
          else delete next[alertType];
          return next;
        });
        setError(err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [urgency],
  );

  return { urgency, loading, saving, error, reload, isUrgent, setUrgent };
}
